import { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";

/**
 * SplitTextReveal — splits text into words and staggers a fade-up reveal.
 * Mode "chars" splits into characters (for dramatic hero effect).
 *
 * Reliability: on iOS Safari the IntersectionObserver behind `whileInView`
 * occasionally fails to fire when the main thread is busy (canvas, GSAP, Lenis).
 * Since `initial={{ y: "110%" }}` with `overflow-hidden` makes the text fully
 * invisible until the animation triggers, a missed fire leaves the user
 * staring at a blank section. We use `useInView` + a hard-coded safety timer
 * that forces the reveal after 1.6s no matter what.
 */
export const SplitTextReveal = ({
  text,
  as: Tag = "h1",
  className = "",
  mode = "words",
  stagger = 0.08,
  delay = 0,
  once = true,
  viewportMargin = "-80px",
}) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: viewportMargin, amount: 0 });
  const [forced, setForced] = useState(false);

  useEffect(() => {
    if (inView) return;
    const t = setTimeout(() => setForced(true), 1600);
    return () => clearTimeout(t);
  }, [inView]);

  const animate = inView || forced;
  const parts = mode === "chars" ? [...text] : text.split(" ");

  return (
    <Tag ref={ref} className={className} aria-label={text}>
      {parts.map((part, i) => (
        <span
          key={i}
          className="inline-block overflow-hidden align-bottom"
          style={{ lineHeight: "inherit" }}
        >
          <motion.span
            className="inline-block"
            initial={{ y: "110%" }}
            animate={animate ? { y: 0 } : { y: "110%" }}
            transition={{
              duration: 0.9,
              delay: delay + i * stagger,
              ease: [0.16, 1, 0.3, 1],
            }}
          >
            {part === " " ? " " : part}
            {mode === "words" && i < parts.length - 1 ? " " : ""}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};
